import useKeydownEventListener from 'hooks/useKeydownEventListener'
import Column from 'layout/Components/Column'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { animated, useSpring } from 'react-spring'
import styled from 'styled-components'
import Box from 'UIKit/Box/Box'
import { BoxProps } from 'UIKit/Box/types'
import { ButtonProps } from 'UIKit/Button/types'
import Popper from 'UIKit/Popper'
import { StyledMenuDropdownContainer, StyledMenuDropdownWrapper } from 'UIKit/Popper/Popper'
import { PaymentChevronRightIcon } from 'views/Payment/MobilePayment/components/styles'
import { SelectContentInjectedProps, SelectInjectedProps, SelectProps } from './types'

export * from './types'

export const SelectContent: React.FC<React.PropsWithChildren<Partial<SelectInjectedProps>> & BoxProps> = ({
  open,
  onOpenChange,
  children,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const eventHandler = useCallback((event) => {
    if (event && event.key === 'ArrowDown') {
      event.preventDefault()

      const next = document.activeElement.nextSibling as HTMLElement
      if (next) {
        next.focus()
      } else {
        const firstChild = containerRef.current.firstChild as HTMLElement
        firstChild.focus()
      }
    } else if (event && event.key === 'ArrowUp') {
      event.preventDefault()

      const previous = document.activeElement.previousSibling as HTMLElement
      if (previous) {
        previous.focus()
      } else {
        const lastChild = containerRef.current.lastChild as HTMLElement
        lastChild.focus()
      }
    } else if (event && event.key === 'Enter') {
      const activeElement = document.activeElement as HTMLElement
      if (activeElement) {
        activeElement.click()
      }
    } else if (event && event.key === 'Escape') {
      onOpenChange(false)
    }
  }, [])

  useKeydownEventListener(eventHandler)

  const styleOpenOption = useSpring({
    config: { duration: 150 },
    opacity: open ? 1 : 0,
    y: open ? 0 : 20,
    from: open ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 },
    onResolve: () => {
      if (!open) {
        onOpenChange(false)
      }
    },
  })

  return (
    <StyledOptionsContainer className="container-content-dropdown" style={{ ...styleOpenOption }} {...props}>
      <StyledOptionsContainerInner>{children}</StyledOptionsContainerInner>
    </StyledOptionsContainer>
  )
}

export const PaymentSelectTrigger: React.FC<
  Partial<SelectInjectedProps> &
    ButtonProps & {
      children?: React.ComponentType<SelectInjectedProps> | React.ReactNode
      asChild?: boolean
      hideIcon?: boolean
    }
> = ({ onOpenChange, open, asChild, children, hideIcon = false, onClick, ...props }) => {
  const child =
    React.isValidElement(children) &&
    React.cloneElement(children, {
      open,
      onOpenChange,
    } as SelectInjectedProps)

  return asChild ? (
    child
  ) : (
    <StyledSelectTrigger
      onClick={(e) => {
        if (onOpenChange) onOpenChange(!open)
        if (onClick) onClick(e)
      }}
      {...props}
    >
      {child}
      {!hideIcon && (
        <StyledExpansitionIconContainer $open={open}>
          <PaymentChevronRightIcon />
        </StyledExpansitionIconContainer>
      )}
    </StyledSelectTrigger>
  )
}
PaymentSelectTrigger.displayName = 'PaymentSelectTrigger'

export const PaymentSelect = <T,>({
  children,
  onOpenChange,
  disabled,
  DropdownContent,
  options,
  onSelect,
  open: defaultOpen,
  ...props
}: SelectProps<T>) => {
  const [toggle, setToggle] = useState(defaultOpen)
  const [openControl, setOpen] = useState(defaultOpen)

  const handleClickSelect = useCallback(
    (value: boolean) => {
      if (value) {
        setOpen(true)
        setToggle(true)
      } else {
        setToggle(false)
      }

      if (onOpenChange) {
        onOpenChange(value)
      }
    },
    [options],
  )

  const handleSelect = useCallback(
    (value: T) => {
      if (onSelect) {
        onSelect(value)
      }
      handleClickSelect(false)
    },
    [onSelect],
  )

  const renderDropdownContent = useMemo(() => {
    if (React.isValidElement(DropdownContent)) {
      return (
        <SelectContent open={toggle} onOpenChange={setOpen}>
          {React.cloneElement(DropdownContent, {
            open: toggle,
            onOpenChange: setOpen,
            options,
            onSelect: handleSelect,
          } as SelectContentInjectedProps<T>)}
        </SelectContent>
      )
    }

    if (typeof DropdownContent === 'function') {
      return (
        <SelectContent open={toggle} onOpenChange={setOpen}>
          <DropdownContent open={toggle} onOpenChange={setOpen} options={options} onSelect={handleSelect} />
        </SelectContent>
      )
    }

    return (
      <SelectContent open={toggle} onOpenChange={setOpen}>
        Empty Content
      </SelectContent>
    )
  }, [toggle, setOpen, DropdownContent, options, handleSelect])

  return (
    <ReStyledMenuDropdownWrapper>
      <Popper
        open={openControl}
        setOpen={handleClickSelect}
        disabled={disabled}
        dropdownContent={renderDropdownContent}
        dropdownPosition="bottom"
        {...props}
      >
        {React.Children.map(children, (child) => {
          if (
            React.isValidElement(child) &&
            typeof child.type !== 'string' &&
            'displayName' in child.type &&
            (child.type as any).displayName === 'PaymentSelectTrigger'
          ) {
            return React.cloneElement(child, {
              open: toggle,
              onOpenChange: handleClickSelect,
            } as React.ComponentProps<typeof PaymentSelectTrigger>)
          }
          return child
        })}
      </Popper>
    </ReStyledMenuDropdownWrapper>
  )
}

const ReStyledMenuDropdownWrapper = styled(StyledMenuDropdownWrapper)`
  ${StyledMenuDropdownContainer} {
    ${({ theme }) => theme.mediaQueries.sm} {
      width: 100%;
    }
  }
`

export const SelectScroll = styled(Column).attrs((props: BoxProps) => ({
  maxHeight: props.maxHeight || '300px',
}))<BoxProps>`
  width: 100%;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  overflow-y: overlay;
  overflow-x: hidden;
`

export const StyledOptionsContainer = styled(animated.div)`
  overflow: hidden;
  box-shadow: 2px 8px 40px 0px rgba(0, 0, 0, 0.25);
  border: none;
  margin-top: 4px;
`
export const StyledOptionsContainerInner = styled(Column)`
  padding: 12px;
  border: 0;
  box-shadow: 0px 4px 10px rgba(50, 50, 60, 0.3);
  border-radius: var(--pm-trigger-radius);
  background: var(--pm-dropdown-color);
`

export const StyledExpansitionIconContainer = styled.div<{ $open: boolean }>`
  transition: ${({ theme }) => theme.transitions.fast};
  transform: ${({ $open }) => ($open ? 'rotate(-90deg) translateY(0)' : 'rotate(90deg)')};

  padding: 0;
  margin: 0;
  height: 12px;
  width: 12px;
  display: flex;
  align-items: center;
  overflow: hidden;

  ${({ theme }) => theme.mediaQueries.md} {
    justify-content: center;
  }
`

export const StyledSelectTrigger = styled(Box).attrs({
  as: 'button',
  height: 'var(--pm-trigger-height, 44px)',
})<
  BoxProps & {
    backgroundHover?: string
    background?: string
  }
>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  gap: 12px;
  padding: 12px;
  width: 100%;
  border-radius: var(--pm-trigger-radius);

  font-size: 14px;
  font-weight: 600;
  outline: none;
  box-shadow: none;

  border: none;
  color: var(--pm-icon-color, ${({ color, theme }) => color || theme.colors.textAlt9});
  background: ${({ background, theme }) => background || `var(--pm-card, ${theme.colors.backgroundAlt14})`};

  &:hover {
    background: ${({ backgroundHover, theme }) =>
      backgroundHover || `var(--pm-card-active, ${theme.colors.backgroundAlt15})`};
  }
`
